/* eslint-disable consistent-return */
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Badge } from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import PageHeader from '~/components/Common/PageHeader';
import Loading from '~/components/Loading';

import api from '~/services/api';
import WebhookDispatchCard from './WebhookDispatchCard';
import DispatchDetailModal from './DispatchDetailModal';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'delivered', label: 'Entregue' },
  { value: 'pending', label: 'Aguardando retry' },
  { value: 'in_flight', label: 'Em processamento' },
  { value: 'retried', label: 'Sobrescrita por retry' },
  { value: 'failed_permanently', label: 'Falhou definitivamente' },
];

const EVENT_OPTIONS = [
  { value: '', label: 'Todos os eventos' },
  { value: 'cart.created', label: 'cart.created' },
  { value: 'cart.in_fraud_analysis', label: 'cart.in_fraud_analysis' },
  { value: 'cart.paid', label: 'cart.paid' },
  { value: 'cart.in_delivery', label: 'cart.in_delivery' },
  { value: 'cart.completed', label: 'cart.completed' },
  { value: 'cart.cancelled', label: 'cart.cancelled' },
  { value: 'cart.payment_rejected', label: 'cart.payment_rejected' },
  { value: 'cart.fraud_rejected', label: 'cart.fraud_rejected' },
  { value: 'cart.awaiting_refund', label: 'cart.awaiting_refund' },
  { value: 'cart.refunded', label: 'cart.refunded' },
  { value: 'cart.chargeback', label: 'cart.chargeback' },
  { value: 'cart.returned', label: 'cart.returned' },
  { value: 'delivery.status_changed', label: 'delivery.status_changed' },
];

const ALL_APPS_OPTION = { value: null, label: 'Todas as aplicações' };
const ALL_WEBHOOKS_OPTION = { value: null, label: 'Todos os webhooks' };

const DISPATCHES_URL = 'administrator/webhook-dispatches';
const PARTNER_APPS_URL = 'administrator/partner-applications';
const PARTNER_WEBHOOKS_URL = 'administrator/partner-webhooks';

/**
 * Trunca URL pra label do select (URLs longas viram ilegíveis no dropdown).
 */
const truncateUrl = (url, max = 60) => {
  if (!url) return '';
  return url.length > max ? `${url.substring(0, max - 3)}...` : url;
};

const WebhookDispatches = () => {
  const location = useLocation();
  const history = useHistory();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Opções dos selects
  const [appOptions, setAppOptions] = useState([ALL_APPS_OPTION]);
  const [webhookOptions, setWebhookOptions] = useState([ALL_WEBHOOKS_OPTION]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);

  // Filtros
  const [appFilter, setAppFilter] = useState(null);
  const [webhookFilter, setWebhookFilter] = useState(null);
  const [eventFilter, setEventFilter] = useState(EVENT_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');

  // Bandeira pra evitar fetch duplo durante inicialização do deeplink
  const [initialized, setInitialized] = useState(false);

  // Toggle pra expandir/recolher filtros secundários (período)
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Modal de detalhe
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const buildParams = useCallback(
    (cursor = null) => {
      const params = { limit: 20 };
      if (cursor) params.cursor = cursor;
      if (appFilter?.value) params.partner_application_id = appFilter.value;
      if (webhookFilter?.value) params.webhook_id = webhookFilter.value;
      if (eventFilter?.value) params.event_type = eventFilter.value;
      if (statusFilter?.value) params.status = statusFilter.value;
      if (createdAfter) params.created_after = createdAfter;
      if (createdBefore) params.created_before = createdBefore;
      return params;
    },
    [
      appFilter,
      webhookFilter,
      eventFilter,
      statusFilter,
      createdAfter,
      createdBefore,
    ]
  );

  const formatItem = d => ({
    ...d,
    formatted_created_at: d.created_at
      ? format(parseISO(d.created_at), "dd/MMM/yy - HH'h'mm'min'", {
          locale: ptBR,
        })
      : '-',
    formatted_delivered_at: d.delivered_at
      ? format(parseISO(d.delivered_at), "dd/MMM/yy - HH'h'mm", {
          locale: ptBR,
        })
      : null,
    formatted_next_retry_at: d.next_retry_at
      ? format(parseISO(d.next_retry_at), "dd/MMM/yy - HH'h'mm", {
          locale: ptBR,
        })
      : null,
  });

  const fetchList = useCallback(
    async (reset = true) => {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const params = buildParams(reset ? null : nextCursor);
        const { data } = await api.get(DISPATCHES_URL, { params });
        const formatted = (data.data || []).map(formatItem);
        if (reset) {
          setItems(formatted);
        } else {
          setItems(prev => [...prev, ...formatted]);
        }
        setNextCursor(data.pagination?.next_cursor || null);
        setHasMore(Boolean(data.pagination?.has_more));
      } catch (err) {
        toast.error(
          err?.response?.data?.message ||
            'Erro ao carregar dispatches de webhook.'
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [buildParams, nextCursor]
  );

  /**
   * Carrega webhooks de uma aplicação específica para popular o select
   * dependente. Se preselectWebhookId vier, já marca esse webhook como
   * selecionado (usado no deeplink).
   */
  const loadWebhooksForApp = useCallback(
    async (appId, preselectWebhookId = null) => {
      if (!appId) {
        setWebhookOptions([ALL_WEBHOOKS_OPTION]);
        setWebhookFilter(null);
        return;
      }

      setLoadingWebhooks(true);
      try {
        const { data } = await api.get(PARTNER_WEBHOOKS_URL, {
          params: { partner_application_id: appId, limit: 100 },
        });
        const opts = (data.data || []).map(w => ({
          value: w.id,
          label: truncateUrl(w.url),
          fullUrl: w.url,
        }));
        setWebhookOptions([ALL_WEBHOOKS_OPTION, ...opts]);

        if (preselectWebhookId) {
          const selected = opts.find(o => o.value === preselectWebhookId);
          if (selected) {
            setWebhookFilter(selected);
          }
        }
      } catch (err) {
        setWebhookOptions([ALL_WEBHOOKS_OPTION]);
      } finally {
        setLoadingWebhooks(false);
      }
    },
    []
  );

  /**
   * Inicialização: carrega apps + processa deeplink (?partner_webhook_id ou
   * ?partner_application_id) e pré-popula filtros. Roda uma única vez.
   */
  useEffect(() => {
    document.title = 'Webhook Dispatches | Viddy';

    const queryParams = new URLSearchParams(location.search);
    const queryWebhookId = queryParams.get('partner_webhook_id');
    const queryAppId = queryParams.get('partner_application_id');

    const initialize = async () => {
      // 1. Carrega lista de aplicações
      let apps = [];
      try {
        const { data } = await api.get(PARTNER_APPS_URL, {
          params: { limit: 100 },
        });
        apps = (data.data || []).map(app => ({
          value: app.id,
          label: `${app.name} (${
            app.environment === 'live' ? 'PROD' : 'TESTE'
          })`,
        }));
      } catch (err) {
        // ignore — sem apps ainda funciona com "Todas"
      }
      setAppOptions([ALL_APPS_OPTION, ...apps]);

      // 2. Processa deeplink
      if (queryWebhookId) {
        // Deeplink mais específico: fetch do webhook pra resolver a app
        try {
          const { data: webhook } = await api.get(
            `${PARTNER_WEBHOOKS_URL}/${queryWebhookId}`
          );
          const webhookAppId =
            webhook?.partner_application_id || webhook?.partner_application?.id;

          if (webhookAppId) {
            const appOpt = apps.find(a => a.value === webhookAppId);
            if (appOpt) {
              setAppFilter(appOpt);
            }
            await loadWebhooksForApp(webhookAppId, queryWebhookId);
          }
        } catch (err) {
          toast.error('Webhook não encontrado ou já removido.');
        }
      } else if (queryAppId) {
        // Deeplink só por aplicação
        const appOpt = apps.find(a => a.value === queryAppId);
        if (appOpt) {
          setAppFilter(appOpt);
          await loadWebhooksForApp(queryAppId);
        }
      }

      setInitialized(true);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Quando appFilter muda manualmente (após inicialização), recarrega
   * webhooks da nova app e limpa o filtro de webhook.
   */
  useEffect(() => {
    if (!initialized) return;
    loadWebhooksForApp(appFilter?.value || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appFilter]);

  /**
   * Refetch quando filtros mudam (apenas depois de inicializado).
   */
  useEffect(() => {
    if (!initialized) return;
    fetchList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    appFilter,
    webhookFilter,
    eventFilter,
    statusFilter,
    createdAfter,
    createdBefore,
  ]);

  const handleOpenDetail = dispatch => {
    setSelectedDispatch(dispatch);
    setDetailOpen(true);
  };

  // Replay cria um NOVO dispatch (preserva o original).
  const handleReplayDone = () => {
    setDetailOpen(false);
    setSelectedDispatch(null);
    fetchList(true);
  };

  /**
   * Limpa todos os filtros e remove query string da URL.
   */
  const handleClearFilters = () => {
    setAppFilter(null);
    setWebhookFilter(null);
    setEventFilter(EVENT_OPTIONS[0]);
    setStatusFilter(STATUS_OPTIONS[0]);
    setCreatedAfter('');
    setCreatedBefore('');
    if (location.search) {
      history.replace('/webhooks-dispatches');
    }
  };

  const hasActiveFilters =
    Boolean(appFilter?.value) ||
    Boolean(webhookFilter?.value) ||
    Boolean(eventFilter?.value) ||
    Boolean(statusFilter?.value) ||
    Boolean(createdAfter) ||
    Boolean(createdBefore);

  // Conta quantos filtros secundários (período) estão ativos pra badge
  const moreFiltersCount = (createdAfter ? 1 : 0) + (createdBefore ? 1 : 0);

  return (
    <div className="page-content">
      <Container className="container new-layout wide">
        <PageHeader
          pageTitle="DISPATCHES DE WEBHOOK"
          responsivePosition="start"
          desktopPosition="start"
        />

        {/* Banner quando filtrado por webhook específico via deeplink */}
        {webhookFilter?.value && (
          <Alert
            color="info"
            className="d-flex align-items-center justify-content-between mb-3"
            style={{ backgroundColor: '#eef4ff', borderColor: '#3b82f6' }}
          >
            <div className="d-flex align-items-center">
              <i
                className="las la-filter mr-2"
                style={{ fontSize: '1.5rem', color: '#3b82f6' }}
              />
              <div>
                <div className="font-weight-600">
                  Filtrando dispatches deste webhook
                </div>
                <code className="font-size-12 text-muted">
                  {webhookFilter.fullUrl || webhookFilter.label}
                </code>
              </div>
            </div>
            <Button
              size="sm"
              color="info"
              outline
              onClick={() => setWebhookFilter(null)}
            >
              Limpar
            </Button>
          </Alert>
        )}

        <Row className="w-100 mx-0 mb-2 d-flex flex-row align-items-center">
          <Col className="px-0 d-flex flex-row align-items-center flex-wrap">
            <span className="text-left d-inline text-muted font-size-14 mr-3">
              Filtros
            </span>

            <div
              style={{ minWidth: 260, marginRight: 8 }}
              className="mb-2 mb-lg-0"
            >
              <Select
                value={appFilter || appOptions[0] || null}
                onChange={opt => setAppFilter(opt?.value ? opt : null)}
                options={appOptions}
                placeholder="Aplicação"
                isSearchable
                classNamePrefix="select"
              />
            </div>

            {/* Filtro de webhook só aparece quando uma aplicação está selecionada */}
            {appFilter?.value && (
              <div
                style={{ minWidth: 260, marginRight: 8 }}
                className="mb-2 mb-lg-0"
              >
                <Select
                  value={webhookFilter || webhookOptions[0] || null}
                  onChange={opt => setWebhookFilter(opt?.value ? opt : null)}
                  options={webhookOptions}
                  placeholder="Webhook"
                  isSearchable
                  isLoading={loadingWebhooks}
                  classNamePrefix="select"
                />
              </div>
            )}

            <div
              style={{ minWidth: 220, marginRight: 8 }}
              className="mb-2 mb-lg-0"
            >
              <Select
                value={eventFilter}
                onChange={setEventFilter}
                options={EVENT_OPTIONS}
                placeholder="Evento"
                classNamePrefix="select"
              />
            </div>

            <div
              style={{ minWidth: 200, marginRight: 8 }}
              className="mb-2 mb-lg-0"
            >
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                options={STATUS_OPTIONS}
                placeholder="Status"
                classNamePrefix="select"
              />
            </div>

            {/* Toggle "Mais filtros" — expande filtros secundários (período) */}
            <Button
              color="link"
              size="sm"
              className="mb-2 mb-lg-0 text-decoration-none d-flex align-items-center"
              style={{
                marginRight: 8,
                color: '#3b82f6',
                fontSize: 13,
                padding: '0 8px',
                height: 38,
              }}
              onClick={() => setShowMoreFilters(prev => !prev)}
            >
              <i className="las la-sliders-h mr-1" style={{ fontSize: 16 }} />
              Mais filtros
              {moreFiltersCount > 0 && (
                <Badge
                  pill
                  className="ml-2"
                  style={{
                    backgroundColor: '#3b82f6',
                    color: '#fff',
                    fontSize: 10,
                    padding: '2px 6px',
                  }}
                >
                  {moreFiltersCount}
                </Badge>
              )}
              <i
                className={`las la-angle-${
                  showMoreFilters ? 'up' : 'down'
                } ml-1`}
                style={{ fontSize: 12 }}
              />
            </Button>

            {hasActiveFilters && (
              <Button
                color="secondary"
                outline
                size="sm"
                className="mb-2 mb-lg-0 d-flex align-items-center"
                style={{ height: 38 }}
                onClick={handleClearFilters}
              >
                <i className="las la-times mr-1" style={{ fontSize: 14 }} />
                Limpar filtros
              </Button>
            )}
          </Col>
        </Row>

        {/* Linha 2 — Filtros secundários (Período), colapsável */}
        {showMoreFilters && (
          <Row className="w-100 mx-0 mb-4 d-flex flex-row align-items-center">
            <Col
              className="px-3 py-2 d-flex flex-row align-items-center flex-wrap"
              style={{
                backgroundColor: '#f8fafc',
                borderRadius: 6,
                border: '1px solid #e5e7eb',
              }}
            >
              <span className="d-inline text-muted font-size-14 mr-3 font-weight-500">
                Período
              </span>

              <div
                className="mb-2 mb-lg-0 d-flex align-items-center"
                style={{ marginRight: 12 }}
              >
                <span className="text-muted font-size-13 mr-2">De</span>
                <input
                  type="date"
                  value={createdAfter}
                  onChange={e => setCreatedAfter(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ width: 150, height: 38 }}
                />
              </div>

              <div
                className="mb-2 mb-lg-0 d-flex align-items-center"
                style={{ marginRight: 12 }}
              >
                <span className="text-muted font-size-13 mr-2">Até</span>
                <input
                  type="date"
                  value={createdBefore}
                  onChange={e => setCreatedBefore(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ width: 150, height: 38 }}
                />
              </div>

              {moreFiltersCount > 0 && (
                <Button
                  color="link"
                  size="sm"
                  className="text-decoration-none d-flex align-items-center"
                  style={{ color: '#6b7280', padding: '0 8px' }}
                  onClick={() => {
                    setCreatedAfter('');
                    setCreatedBefore('');
                  }}
                >
                  <i className="las la-times mr-1" />
                  Limpar período
                </Button>
              )}
            </Col>
          </Row>
        )}

        {loading ? (
          <Loading />
        ) : (
          <>
            {items.length > 0 ? (
              <>
                <Row className="w-100 mx-0 mb-4 d-none d-lg-flex px-3">
                  <Col xs={3} align="start">
                    <div className="new-layout-table-column-label">Evento</div>
                  </Col>
                  <Col xs={3} align="start">
                    <div className="new-layout-table-column-label">
                      Aplicação / URL
                    </div>
                  </Col>
                  <Col xs={2} align="start">
                    <div className="new-layout-table-column-label">Status</div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">HTTP</div>
                  </Col>
                  <Col xs={1} align="start">
                    <div className="new-layout-table-column-label">
                      Tentativas
                    </div>
                  </Col>
                  <Col xs={2} align="start">
                    <div className="new-layout-table-column-label">Criado</div>
                  </Col>
                </Row>

                {items.map(dispatch => (
                  <Row className="w-100 mx-0" key={dispatch.id}>
                    <Col className="px-0" xs={12}>
                      <WebhookDispatchCard
                        dispatch={dispatch}
                        onOpenDetail={handleOpenDetail}
                      />
                    </Col>
                  </Row>
                ))}

                {hasMore && (
                  <Row className="w-100 mx-0 mb-4">
                    <Col className="px-0 d-flex justify-content-center">
                      <Button
                        color="primary"
                        outline
                        size="lg"
                        disabled={loadingMore}
                        onClick={() => fetchList(false)}
                      >
                        {loadingMore ? 'Carregando...' : 'Carregar mais'}
                      </Button>
                    </Col>
                  </Row>
                )}
              </>
            ) : (
              <h3>Nenhum dispatch encontrado.</h3>
            )}
          </>
        )}
      </Container>

      <DispatchDetailModal
        show={detailOpen}
        setShow={setDetailOpen}
        dispatch={selectedDispatch}
        onReplayed={handleReplayDone}
      />
    </div>
  );
};

export default WebhookDispatches;
